import { useState, useMemo, useEffect, lazy, Suspense } from "react";
import { Routes, Route, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "./hooks/useAuth";
import { useAgency } from "./hooks/useAgency";
import { useJobs } from "./hooks/useJobs";
import { useUsers } from "./hooks/useUsers";
import { useEntities } from "./hooks/useEntities";
import { useRateTiers } from "./hooks/useRateTiers";
import { useAuditLog } from "./hooks/useAuditLog";
import { useOfficerAuditLog } from "./hooks/useOfficerAuditLog";
import { useNotifications } from "./hooks/useNotifications";
import { getRoleFlags } from "./hooks/useRole";
import { STATUS_PENDING, STATUS_INACTIVE, STATUS_BLOCKED, STATUS_REJECTED } from "./constants";
import { AppProvider } from "./context/AppContext";
import { ToastProvider, ToastViewport, useToast } from "./context/ToastContext";
import { OfflineProvider, OfflineBanner } from "./context/OfflineContext";
import { ExperimentalFeaturesProvider, useExperimentalFeatures } from "./context/ExperimentalFeaturesContext";
import { FEATURE_AGENCY_INVOICING } from "./experimentalFeatures";

import ErrorBoundary from "./components/ErrorBoundary";
import ErrorBanner from "./components/ErrorBanner";
import NotFoundPage from "./components/NotFoundPage";
import "./App.css";
import LandingPage from "./components/LandingPage";
import JobForm from "./components/JobForm";
import ConfirmDialog from "./components/ConfirmDialog";
import InstallPrompt from "./components/InstallPrompt";
import AppShell from "./components/AppShell";
import TestSessionBanner from "./components/TestSessionBanner";

import { DashboardPage, CalendarPage, JobDetailPage, ProfilePage, AttendancePage } from "./pages/JobPages";

// Lazy-loaded: only fetched when the user actually navigates to these routes.
// Cuts the main bundle size for officers (the majority) who never load admin
// or entity/platform UI.
const FeaturesPage = lazy(() => import("./components/FeaturesPage"));
const HowToPage = lazy(() => import("./components/HowToPage"));
const TermsPage = lazy(() => import("./components/TermsPage"));
const OfficersPage = lazy(() => import("./pages/OfficersPage"));
const JobsHubPage = lazy(() => import("./pages/JobsHubPage"));
const AuditHubPage = lazy(() => import("./pages/AuditHubPage"));
const EntityHubPage = lazy(() => import("./pages/EntityHubPage"));
const OfficerAuditPage = lazy(() => import("./pages/OfficerAuditPage"));
const PublicRequestForm = lazy(() => import("./components/public/PublicRequestForm"));
const PublicRequestSuccess = lazy(() => import("./components/public/PublicRequestSuccess"));
const EntityRegisterPage = lazy(() => import("./components/entity/EntityRegisterPage"));
const EntityPortalApp = lazy(() => import("./components/entity/EntityPortalApp"));
const PlatformAdminApp = lazy(() => import("./components/platform/PlatformAdminApp"));
const InvoiceTemplateBuilder = lazy(() => import("./components/InvoiceTemplateBuilder"));

function RouteFallback() {
  return <div style={{ padding: "2rem", textAlign: "center", color: "#6b7280" }}>Loading…</div>;
}

export default function App() {
  return (
    <ErrorBoundary>
      <ToastProvider>
        <OfflineProvider>
          <AppInner />
          <ToastViewport />
        </OfflineProvider>
      </ToastProvider>
    </ErrorBoundary>
  );
}

function AppInner() {
  const { currentUser, loading: authLoading, login, register, registerOrganization, logout, resetPassword, updateAuthEmail, changePassword, pendingApproval, setPendingApproval } = useAuth();
  const { showSuccess, showError } = useToast();

  const loggedIn = !!currentUser;
  const agencyId = currentUser?.agencyId || null;
  // Role-aware data scoping: officers don't need the entity/tier/audit feeds,
  // and only need a name-lookup for active fellow officers (not pending /
  // inactive / blocked accounts). Cuts Firestore reads and limits PII exposure.
  const roleFlags = getRoleFlags(currentUser?.role);
  const isOfficerRole = loggedIn && roleFlags.isOfficer;
  const isAdminRole = loggedIn && roleFlags.isAdmin;
  const canManageData = loggedIn && roleFlags.canManageData;

  const { agency, loading: agencyLoading, error: agencyError, updateAgency } = useAgency(agencyId);

  const {
    jobs, loading: jobsLoading, error: jobsError, createJob, createRecurringJobs, updateJob, cancelJob, deleteJob, signup, withdraw,
    joinWaitlist, leaveWaitlist, addOfficer, removeOfficer,
    blockOfficer: blockOfficerFromJob, sendAnnouncement, recordAttendance,
  } = useJobs(loggedIn, agencyId);
  const {
    users, loading: usersLoading, error: usersError, approveOfficer, rejectOfficer,
    inactivateOfficer, blockOfficer: blockUser, activateOfficer, deleteOfficer,
    updateProfile, createOfficer,
  } = useUsers(loggedIn, agencyId, { activeOnly: isOfficerRole });
  const { entities, loading: entitiesLoading, error: entitiesError, createEntity, updateEntity, deactivateEntity } = useEntities(canManageData, agencyId);
  const { tiers, loading: tiersLoading, error: tiersError, createTier, updateTier, deleteTier } = useRateTiers(canManageData, agencyId);
  const { log, loading: auditLoading, error: auditError, addEntry, fetchAll: fetchAllAuditEntries, hasMore, hasPrev, page: auditPage, loadNextPage, loadPrevPage } = useAuditLog(isAdminRole, agencyId);
  const officerAudit = useOfficerAuditLog(isOfficerRole, agencyId, currentUser?.id);
  const { notifications, unreadCount, error: notificationsError, markRead, markAllRead } = useNotifications(currentUser?.id);
  // Experimental-feature switches: live only for platform admins and test
  // sessions; inert (no reads, everything off) for regular users.
  const experimental = useExperimentalFeatures(currentUser);

  const navigate = useNavigate();
  const location = useLocation();
  // Test-environment handoff: "Operate as" swaps the signed-in account while
  // the browser sits on the platform panel's /test-environment route. Once
  // the new account has loaded, move it to its dashboard.
  const isTestSession = currentUser?.testSession === true;
  const onTestEnvRoute = location.pathname.startsWith("/test-environment");
  useEffect(() => {
    if (isTestSession && onTestEnvRoute) navigate("/", { replace: true });
  }, [isTestSession, onTestEnvRoute, navigate]);
  const [editingJob, setEditingJob] = useState<any>(null);
  const [attendanceJob, setAttendanceJob] = useState<any>(null);
  const [confirmAction, setConfirmAction] = useState<{ title: string; message: string; onConfirm: () => void } | null>(null);

  const agencyStyle = useMemo(() => {
    if (!agency) return {};
    return {
      "--agency-primary": agency.primaryColor || "#0f3460",
      "--agency-secondary": agency.secondaryColor || "#16213e",
    };
  }, [agency]);

  // Split context into stable sub-objects so changes in one domain
  // don't invalidate the entire context value for all consumers.
  const uiActions = useMemo(() => ({
    showSuccess, showError, setConfirmAction, setEditingJob, setAttendanceJob,
  }), [showSuccess, showError]);

  const authActions = useMemo(() => ({
    updateAuthEmail, changePassword,
  }), [updateAuthEmail, changePassword]);

  const jobActions = useMemo(() => ({
    createJob, createRecurringJobs, updateJob, cancelJob, deleteJob, signup, withdraw,
    joinWaitlist, leaveWaitlist, addOfficer, removeOfficer,
    blockOfficer: blockOfficerFromJob, sendAnnouncement, recordAttendance,
  }), [
    createJob, createRecurringJobs, updateJob, cancelJob, deleteJob, signup, withdraw,
    joinWaitlist, leaveWaitlist, addOfficer, removeOfficer,
    blockOfficerFromJob, sendAnnouncement, recordAttendance,
  ]);

  const userActions = useMemo(() => ({
    approveOfficer, rejectOfficer, inactivateOfficer, blockUser,
    activateOfficer, deleteOfficer, updateProfile, createOfficer,
  }), [
    approveOfficer, rejectOfficer, inactivateOfficer, blockUser,
    activateOfficer, deleteOfficer, updateProfile, createOfficer,
  ]);

  const dataActions = useMemo(() => ({
    updateAgency,
    createEntity, updateEntity, deactivateEntity,
    createTier, updateTier, deleteTier,
    addEntry,
  }), [
    updateAgency,
    createEntity, updateEntity, deactivateEntity,
    createTier, updateTier, deleteTier,
    addEntry,
  ]);

  const appContextValue = useMemo(() => ({
    currentUser, agencyId,
    ...uiActions,
    ...authActions,
    ...jobActions,
    ...userActions,
    ...dataActions,
  }), [currentUser, agencyId, uiActions, authActions, jobActions, userActions, dataActions]);

  if (authLoading) {
    return (
      <div className="loading-screen">
        <div className="spinner" />
        <p className="loading-text">Loading...</p>
      </div>
    );
  }

  // Platform admins get a completely separate admin portal
  if (currentUser?.platformAdmin) {
    return (
      <ErrorBoundary inline>
        <ExperimentalFeaturesProvider value={experimental}>
          <Suspense fallback={<RouteFallback />}>
            <PlatformAdminApp currentUser={currentUser} onLogout={logout} />
          </Suspense>
        </ExperimentalFeaturesProvider>
      </ErrorBoundary>
    );
  }

  if (!currentUser) {
    return (
      <ErrorBoundary inline>
        <Suspense fallback={<RouteFallback />}>
          <Routes>
            <Route path="/features" element={<FeaturesPage />} />
            <Route path="/how-to" element={<HowToPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/register/entity" element={<EntityRegisterPage />} />
            <Route path="/request/success" element={<PublicRequestSuccess />} />
            <Route path="/request/:agencySlug" element={<PublicRequestForm />} />
            <Route path="/request" element={<PublicRequestForm />} />
            <Route path="*" element={<LandingPage onLogin={login} onRegister={register} onRegisterOrganization={registerOrganization} onResetPassword={resetPassword} pendingApproval={pendingApproval} setPendingApproval={setPendingApproval} />} />
          </Routes>
        </Suspense>
      </ErrorBoundary>
    );
  }

  if (!agencyLoading && agencyId && !agency) {
    // Distinguish "agency doc doesn't exist" (deactivated/removed) from
    // "agency fetch failed" (permission denied, network error, etc.) — the
    // generic "Agency Unavailable" message misleads users about the cause.
    if (agencyError) {
      return (
        <BlockedAccountScreen
          title="Couldn't load agency"
          message={`${agencyError} Please try again or contact your administrator if the problem persists.`}
          onLogout={logout}
        />
      );
    }
    return <BlockedAccountScreen title="Agency Unavailable" message="Your agency is no longer active or has been removed. Please contact your administrator." onLogout={logout} />;
  }

  // Block inactive/blocked/rejected users from using the app
  const lockedOut = [STATUS_INACTIVE, STATUS_BLOCKED, STATUS_REJECTED].includes(currentUser.status);
  if (lockedOut) {
    const statusMessages = {
      [STATUS_INACTIVE]: "Your account has been deactivated.",
      [STATUS_BLOCKED]: "Your account has been blocked.",
      [STATUS_REJECTED]: "Your registration was not approved.",
    };
    return (
      <BlockedAccountScreen
        title="Account Unavailable"
        message={`${statusMessages[currentUser.status] || "Your account is not active."} Please contact your administrator for assistance.`}
        onLogout={logout}
      />
    );
  }

  // Entity users get a completely separate portal
  if (roleFlags.isEntity) {
    return (
      <ErrorBoundary inline>
        <ExperimentalFeaturesProvider value={experimental}>
          {currentUser.testSession && (
            <TestSessionBanner key={currentUser.id} currentUser={currentUser} onLogout={logout} />
          )}
          <Suspense fallback={<RouteFallback />}>
            <EntityPortalApp currentUser={currentUser} onLogout={logout} />
          </Suspense>
        </ExperimentalFeaturesProvider>
      </ErrorBoundary>
    );
  }

  const { isAdmin, isSecretary, canManageData: canManageJobs } = roleFlags;
  const pendingCount = users.filter((u) => u.status === STATUS_PENDING).length;

  const navItems = [
    { to: "/", label: "Dashboard", icon: "🏠" },
    { to: "/calendar", label: "Calendar", icon: "📅" },
    ...(isAdmin ? [
      { to: "/officers", label: `Officers${pendingCount ? ` (${pendingCount})` : ""}`, icon: "👮" },
      { to: "/jobs-hub", label: "Jobs", icon: "📋" },
      { to: "/entities-hub", label: "Organizations", icon: "🏢" },
      { to: "/audit-hub", label: "Audit", icon: "📜" },
    ] : isSecretary ? [
      { to: "/jobs-hub", label: "Jobs", icon: "📋" },
    ] : [
      { to: "/audit", label: "Audit", icon: "📜" },
    ]),
    { to: "/profile", label: "My Profile", icon: "👤" },
  ];

  const pageTitle = (() => {
    if (location.pathname.startsWith("/jobs-hub")) return "Jobs";
    if (location.pathname.startsWith("/entities-hub")) return "Organizations";
    if (location.pathname.startsWith("/audit-hub")) return "Audit";
    const match = navItems.find((item) =>
      item.to === "/" ? location.pathname === "/" : location.pathname.startsWith(item.to)
    );
    return match ? match.label.replace(/ \(\d+\)$/, "") : "OfficerOnDuty";
  })();

  const handleUpdateJob = async (form) => {
    // Capture editingJob locally so a concurrent setEditingJob(null) can't race us.
    const target = editingJob;
    if (!target) return;
    try {
      const trackFields = ["title", "entity", "date", "startTime", "endTime", "location", "payRate", "slotsTotal", "details", "isPrivate"];
      const changes = {};
      for (const key of trackFields) {
        const oldVal = target[key] ?? "";
        const newVal = form[key] ?? "";
        if (String(oldVal) !== String(newVal)) {
          changes[key] = { from: oldVal, to: newVal };
        }
      }
      await updateJob(target.id, form);
      await addEntry(currentUser, "job_updated", target.id, form.title, "Job updated", null, null, Object.keys(changes).length > 0 ? changes : null);
      showSuccess("Job updated successfully.");
      setEditingJob(null);
      navigate(canManageJobs ? "/jobs-hub/manage" : "/");
    } catch (err) {
      showError("Failed to update job: " + err.message);
      throw err;
    }
  };

  return (
    <AppProvider value={appContextValue}>
    <ExperimentalFeaturesProvider value={experimental}>
      {currentUser.testSession && (
        <TestSessionBanner key={currentUser.id} currentUser={currentUser} agencyName={agency?.name} onLogout={logout} />
      )}
      <OfflineBanner />
      <LoadErrorStack
        errors={[
          { label: "jobs", error: jobsError },
          { label: "officers", error: usersError },
          { label: "organizations", error: entitiesError },
          { label: "rate tiers", error: tiersError },
          { label: "audit log", error: auditError },
          { label: "officer audit log", error: officerAudit.error },
          { label: "notifications", error: notificationsError },
          { label: "agency", error: agencyError },
        ]}
      />

      <AppShell
        agency={agency}
        agencyStyle={agencyStyle}
        currentUser={currentUser}
        navItems={navItems}
        pageTitle={pageTitle}
        jobs={jobs}
        users={users}
        entities={entities}
        notifications={notifications}
        unreadCount={unreadCount}
        markRead={markRead}
        markAllRead={markAllRead}
        onLogout={logout}
      >
        {attendanceJob ? (
          <AttendancePage job={attendanceJob} users={users} onDone={() => setAttendanceJob(null)} />
        ) : editingJob ? (
          <div className="page-container">
            <JobForm
              entities={entities}
              tiers={tiers}
              editingJob={editingJob}
              onSubmit={handleUpdateJob}
              onCancel={() => { setEditingJob(null); navigate(canManageJobs ? "/jobs-hub/manage" : "/"); }}
            />
          </div>
        ) : (
          <ErrorBoundary inline>
            <Suspense fallback={<RouteFallback />}>
            <Routes>
              <Route path="/" element={<DashboardPage jobs={jobs} users={users} currentUser={currentUser} jobsLoading={jobsLoading} />} />
              <Route path="/profile" element={<ProfilePage currentUser={currentUser} />} />
              {isAdmin && (
                <>
                  {experimental.isEnabled(FEATURE_AGENCY_INVOICING) && (
                    <Route path="/invoice-template" element={<InvoiceTemplateBuilder />} />
                  )}
                  <Route path="/officers" element={<OfficersPage users={users} jobs={jobs} usersLoading={usersLoading} />} />
                  <Route path="/entities-hub/*" element={
                    <EntityHubPage
                      entities={entities}
                      entitiesLoading={entitiesLoading}
                      jobs={jobs}
                      users={users}
                      agencyName={agency?.name}
                      agency={agency}
                      currentUser={currentUser}
                    />
                  } />
                  <Route path="/audit-hub/*" element={
                    <AuditHubPage
                      jobs={jobs}
                      users={users}
                      log={log}
                      auditLoading={auditLoading}
                      hasMore={hasMore}
                      hasPrev={hasPrev}
                      auditPage={auditPage}
                      onNextPage={loadNextPage}
                      onPrevPage={loadPrevPage}
                      onFetchAll={fetchAllAuditEntries}
                    />
                  } />
                </>
              )}
              {canManageJobs && (
                <Route path="/jobs-hub/*" element={
                  <JobsHubPage
                    jobs={jobs}
                    users={users}
                    entities={entities}
                    tiers={tiers}
                    jobsLoading={jobsLoading}
                    tiersLoading={tiersLoading}
                    agencyName={agency?.name}
                    agency={agency}
                    currentUser={currentUser}
                  />
                } />
              )}
              {!isAdmin && (
                <Route path="/audit" element={
                  <OfficerAuditPage
                    jobs={jobs}
                    currentUser={currentUser}
                    log={officerAudit.log}
                    loading={officerAudit.loading}
                    hasMore={officerAudit.hasMore}
                    hasPrev={officerAudit.hasPrev}
                    page={officerAudit.page}
                    onNextPage={officerAudit.loadNextPage}
                    onPrevPage={officerAudit.loadPrevPage}
                    onFetchAll={officerAudit.fetchAll}
                  />
                } />
              )}
              <Route path="/calendar" element={<CalendarPage jobs={jobs} currentUser={currentUser} />} />
              <Route path="/jobs/:jobId" element={<JobDetailPage jobs={jobs} currentUser={currentUser} jobsLoading={jobsLoading} />} />
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
            </Suspense>
          </ErrorBoundary>
        )}
      </AppShell>

      <InstallPrompt />

      {confirmAction && (
        <ConfirmDialog
          title={confirmAction.title}
          message={confirmAction.message}
          confirmLabel="Yes, proceed"
          onConfirm={confirmAction.onConfirm}
          onCancel={() => setConfirmAction(null)}
        />
      )}
    </ExperimentalFeaturesProvider>
    </AppProvider>
  );
}

function LoadErrorStack({ errors }: { errors: { label: string; error: string | null | undefined }[] }) {
  const active = errors.filter((e) => e.error);
  if (active.length === 0) return null;
  return (
    <div style={{ padding: "0 16px", marginTop: 12 }}>
      {active.map(({ label, error }) => (
        <ErrorBanner key={label} message={`Couldn't load ${label}: ${error}`} />
      ))}
    </div>
  );
}

function BlockedAccountScreen({ title, message, onLogout }) {
  return (
    <div className="loading-screen">
      <div style={{ background: "#fff", borderRadius: 12, padding: "32px 28px", maxWidth: 400, textAlign: "center" }}>
        <h2 style={{ margin: "0 0 12px", color: "#b91c1c" }}>{title}</h2>
        <p style={{ color: "#6b7280", fontSize: 14, margin: "0 0 20px" }}>{message}</p>
        <button onClick={onLogout} style={{ padding: "10px 24px", background: "#1a1a2e", color: "#fff", border: "none", borderRadius: 8, cursor: "pointer", fontSize: 14 }}>
          Sign Out
        </button>
      </div>
    </div>
  );
}
